import { UIEvent, useCallback, useEffect, useRef, useState } from 'react';

interface UseSideScrollOptions {
	increment?: number;
	onScroll?: (event: UIEvent) => void;
	ref?: React.MutableRefObject<any>;
}

export const useSideScroll = ({ increment = 250, onScroll, ref }: UseSideScrollOptions = {}) => {
	const observerRef = useRef<ResizeObserver | null>(null);
	const [container, setContainer] = useState<HTMLElement | null>(null);
	const [allowedScroll, setAllowedScroll] = useState({
		canScrollLeft: false,
		canScrollRight: false,
	});

	const determineAllowedScroll = useCallback(() => {
		if (!container) {
			return;
		}

		const { scrollLeft, offsetWidth, scrollWidth } = container;

		// I'm not sure why this is the case, but even though this function runs in a useEffect if you unmount a component using this
		// hook and then remount it the container size is always 0. I had to add a resize observer in that case to watch for the size
		// of the container to change and trigger a re-calculation of the allowed scroll.
		if (!offsetWidth && !observerRef.current) {
			const observer = new ResizeObserver(() => {
				determineAllowedScroll();
				observer.disconnect();
				observerRef.current = null;
			});

			observer.observe(container);
			observerRef.current = observer;
		}

		const canScrollLeft = scrollLeft > 0;
		const canScrollRight = scrollLeft + offsetWidth < scrollWidth;

		// Only update the state if the values have changed to prevent unnecessary re-renders
		if (canScrollLeft !== allowedScroll.canScrollLeft || canScrollRight !== allowedScroll.canScrollRight) {
			setAllowedScroll({ canScrollLeft, canScrollRight });
		}
	}, [allowedScroll, container]);

	const getButtonProps = useCallback(
		(direction: 'left' | 'right') => {
			const onClick = () => {
				if (!container) {
					return;
				}

				const { scrollLeft } = container;

				const newScroll = direction === 'left' ? scrollLeft - increment : scrollLeft + increment;

				container.scrollTo({
					left: newScroll,
					behavior: 'smooth',
				});
			};

			return { onClick };
		},
		[container, increment]
	);

	const getContainerProps = useCallback(() => {
		return {
			onScroll: (e: UIEvent) => {
				determineAllowedScroll();
				onScroll?.(e);
			},
			ref: (el: any) => {
				setContainer(el);

				if (ref) {
					ref.current = el;
				}
			},
		};
	}, [determineAllowedScroll, onScroll, ref]);

	useEffect(() => {
		determineAllowedScroll();

		window.addEventListener('resize', determineAllowedScroll);

		return () => {
			window.removeEventListener('resize', determineAllowedScroll);
		};
	}, [container, determineAllowedScroll]);

	// Sometimes the resize event doesn't fire when content is added to the container. This is a workaround to ensure the scroll is recalculated.
	useEffect(() => {
		const timeout = setTimeout(() => {
			determineAllowedScroll();
		}, 1000);

		return () => clearTimeout(timeout);
	}, [determineAllowedScroll]);

	useEffect(() => {
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, []);

	return {
		...allowedScroll,
		getButtonProps,
		getContainerProps,
	};
};
